/**
 * Wishlist — account-gated, cross-device.
 *
 * The wishlist now lives in the customer profile (customer metafield custom.wishlist),
 * written server-side through the /apps/wishlist app-proxy and read back into JS via
 * snippets/wishlist-bootstrap.liquid (window.__TOS_WISHLIST_STATE / __TOS_WISHLIST_SERVER).
 *
 *  - Logged OUT: nothing is saved. Clicking any heart opens the branded login modal
 *    and stashes the clicked item so it is saved right after login.
 *  - Logged IN: the server value is the source of truth. Changes are cached locally
 *    (instant UI) and debounce-POSTed to the server (cross-device). A per-customer
 *    localStorage cache bridges the same device while a write propagates / if the
 *    backend endpoint is not live yet (graceful degradation).
 */
(function () {
  /* ── Config ── */
  var LEGACY_KEY = 'threohsix_wishlist'; // pre-login anonymous localStorage
  var LEGACY_COOKIE = 'threohsix_wl';    // pre-login anonymous cookie backup
  var PROXY = '/apps/wishlist';

  /* ── Login state + server seed (from snippets/wishlist-bootstrap.liquid) ──
     SERVER: array  -> backend is managing this customer's list (source of truth)
             null   -> logged in but no metafield yet (backend empty / not live) -> use local cache
             undef  -> logged out */
  var STATE = window.__TOS_WISHLIST_STATE || { loggedIn: false, customerId: null };
  var SERVER = (typeof window.__TOS_WISHLIST_SERVER !== 'undefined') ? window.__TOS_WISHLIST_SERVER : undefined;
  var CID = (STATE && STATE.customerId != null) ? String(STATE.customerId) : null;
  var CACHE_KEY = CID ? ('threohsix_wl_c_' + CID) : LEGACY_KEY;
  var SYNCED_FLAG = CID ? ('threohsix_wl_synced_' + CID) : 'threohsix_wl_synced';

  /* ── In-memory source of truth (used when logged in) ── */
  var mirror = [];

  /* ── storage helpers ── */
  function lsGet(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }
  function lsDel(k) { try { localStorage.removeItem(k); } catch (e) {} }
  function parseArr(s) { if (!s) return null; try { var a = JSON.parse(s); return Array.isArray(a) ? a : null; } catch (e) { return null; } }

  function getCookie(name) { var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)')); return m ? decodeURIComponent(m[1]) : null; }
  function setCookie(name, val, days) { var d = new Date(); d.setTime(d.getTime() + (days || 1) * 864e5); document.cookie = name + '=' + encodeURIComponent(val) + ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax'; }
  function delCookie(name) { document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax'; }

  /* pre-login anonymous list (migrated into the account on first login) */
  function legacyAnon() { return parseArr(lsGet(LEGACY_KEY)) || parseArr(getCookie(LEGACY_COOKIE)) || []; }
  function clearLegacyAnon() { lsDel(LEGACY_KEY); delCookie(LEGACY_COOKIE); }

  /* per-customer cache (same-device fast path / offline bridge) */
  function readCache() { return parseArr(lsGet(CACHE_KEY)) || []; }
  function writeCache(items) { if (STATE.loggedIn) lsSet(CACHE_KEY, JSON.stringify(items)); }

  /* pending item stashed across the login redirect (cookie survives the auth hop) */
  function stashPending(p) { try { setCookie('threohsix_wl_pending', JSON.stringify(p), 1); } catch (e) {} }
  function readPending() { var raw = getCookie('threohsix_wl_pending'); if (!raw) return null; try { var o = JSON.parse(raw); return (o && o.variantId) ? o : null; } catch (e) { return null; } }
  function clearPending() { delCookie('threohsix_wl_pending'); }

  /* merge helper: append items not already present (by variantId). Returns true if changed. */
  function foldInto(target, items) {
    if (!items || !items.length) return false;
    var seen = {};
    target.forEach(function (i) { if (i && i.variantId != null) seen[String(i.variantId)] = true; });
    var changed = false;
    items.forEach(function (i) {
      if (!i || i.variantId == null) return;
      var v = String(i.variantId);
      if (!seen[v]) { target.push(i); seen[v] = true; changed = true; }
    });
    return changed;
  }

  function readHeartPayload(btn) {
    return {
      handle: btn.getAttribute('data-wishlist-handle'),
      title: btn.getAttribute('data-wishlist-title') || '',
      url: btn.getAttribute('data-wishlist-url') || '',
      image: btn.getAttribute('data-wishlist-image') || '',
      price: btn.getAttribute('data-wishlist-price') || '',
      variantId: btn.getAttribute('data-wishlist-variant') || '',
      variantTitle: btn.getAttribute('data-wishlist-variant-title') || '',
      added: Date.now()
    };
  }

  /* ── core read / write ── */
  function load() { return STATE.loggedIn ? mirror.slice() : []; }
  function save(items) {
    mirror = Array.isArray(items) ? items : [];
    writeCache(mirror);
    scheduleWrite();
  }

  /* ── server write (debounced full-list replace via app proxy) ── */
  var writeTimer = null;
  function scheduleWrite() {
    if (!STATE.loggedIn) return;
    if (writeTimer) clearTimeout(writeTimer);
    writeTimer = setTimeout(pushNow, 1200);
  }
  function pushNow() {
    writeTimer = null;
    if (!STATE.loggedIn) return;
    try {
      window.fetch(PROXY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: mirror }),
        keepalive: true
      }).catch(function () {});
    } catch (e) {}
  }
  /* Flush a pending write before the page unloads so the next page seeds fresh. */
  window.addEventListener('pagehide', function () {
    if (STATE.loggedIn && writeTimer) {
      writeTimer = null;
      try { navigator.sendBeacon(PROXY, new Blob([JSON.stringify({ items: mirror })], { type: 'application/json' })); } catch (e) {}
    }
  });

  /* ── Public API ──
     Identity is the variant id. add/remove/prune are no-ops when logged out (the
     click handler opens the login modal instead). */
  window.Wishlist = {
    get: load,

    isLoggedIn: function () { return !!STATE.loggedIn; },

    has: function (variantId) {
      if (!variantId) return false;
      var vid = String(variantId);
      return load().some(function (i) { return String(i.variantId) === vid; });
    },

    add: function (handle, title, url, image, price, variantId, variantTitle) {
      if (!variantId || !STATE.loggedIn) return;
      var items = load();
      var vid = String(variantId);
      if (items.some(function (i) { return String(i.variantId) === vid; })) return;
      items.push({
        handle: handle,
        title: title,
        url: url,
        image: image,
        price: price,
        variantId: variantId,
        variantTitle: variantTitle,
        added: Date.now()
      });
      save(items);
      if (this._syncNow) this._syncNow(); else this.sync();
    },

    remove: function (variantId) {
      if (!variantId || !STATE.loggedIn) return;
      var vid = String(variantId);
      var items = load().filter(function (i) { return String(i.variantId) !== vid; });
      save(items);
      if (this._syncNow) this._syncNow(); else this.sync();
    },

    /* Save-for-later gate (cart popup): logged out → stash item + wishlist page
       as return target, open the login modal. The cart line stays; it is removed
       after login in initWishlist (sflRemoveCart), so an abandoned login loses
       nothing. Returns true when gated. */
    saveForLater: function (payload, wishlistUrl) {
      if (STATE.loggedIn || !payload || !payload.variantId) return false;
      payload.sflRemoveCart = String(payload.variantId);
      stashPending(payload);
      openLoginModal(wishlistUrl || null);
      return true;
    },

    toggle: function (handle, title, url, image, price, variantId, variantTitle) {
      if (this.has(variantId)) {
        this.remove(variantId);
        return false;
      } else {
        this.add(handle, title, url, image, price, variantId, variantTitle);
        return true;
      }
    },

    count: function () {
      return load().length;
    },

    /* Remove entries whose product no longer exists (the wishlist page passes in the
       items whose card fetch 404'd). Persists to the server via save(). */
    prune: function (deadItems) {
      if (!STATE.loggedIn || !deadItems || !deadItems.length) return;
      var deadVids = {};
      var deadHandles = {};
      deadItems.forEach(function (d) {
        if (!d) return;
        if (d.variantId) deadVids[String(d.variantId)] = true;
        else if (d.handle) deadHandles[d.handle] = true;
      });
      var items = load().filter(function (i) {
        if (i.variantId) return !deadVids[String(i.variantId)];
        return !deadHandles[i.handle];
      });
      save(items);
      if (this._syncNow) this._syncNow(); else this.sync();
    },

    /* ── Sync all UI on the page ── */
    sync: function () {
      var items = load();
      var count = items.length;
      var activeVariants = {};
      items.forEach(function (i) { if (i.variantId) activeVariants[String(i.variantId)] = true; });

      document.querySelectorAll('.wishlist-count').forEach(function (el) {
        el.textContent = count;
        el.toggleAttribute('hidden', count === 0);
      });

      document.querySelectorAll('[data-wishlist-variant]').forEach(function (btn) {
        var v = btn.getAttribute('data-wishlist-variant');
        var active = !!activeVariants[String(v)];
        btn.classList.toggle('wishlist-heart--active', active);
        btn.setAttribute('aria-pressed', active);
      });

      var counter = document.getElementById('WishlistCounter');
      if (counter) counter.textContent = count;
    },

    /* ── Add to Cart via AJAX ──
       Mirrors the theme's own product-form flow: collect the REAL section ids
       via cart:grouped-sections (the cart drawer registers its group id there),
       bundle them in the add request and publish cartUpdate with the rendered
       sections. Rendering by filename ('cart-drawer') would return the section
       with default schema settings and break the editor-configured look. */
    addToCart: function (variantId, btn) {
      if (btn) {
        btn.classList.add('wl-size--loading');
        btn.disabled = true;
      }

      var sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      var body = JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: sectionsToBundle.join(','),
        sections_url: window.location.pathname
      });

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
        body: body
      })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status) {
          if (btn) btn.classList.add('wl-size--error');
          setTimeout(function () { if (btn) btn.classList.remove('wl-size--error'); }, 1500);
          return;
        }

        if (btn) btn.classList.add('wl-size--added');
        setTimeout(function () { if (btn) btn.classList.remove('wl-size--added'); }, 2000);

        var cartDrawer = document.querySelector('cart-drawer');

        // Publish cartUpdate with the bundled sections so CartItems swaps in
        // the drawer body/footer rendered with the real section settings.
        if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.publish && data.sections) {
          fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
            cart.sections = data.sections;
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'wishlist-atc',
              productVariantId: variantId,
              cart: cart
            });
            if (cartDrawer && typeof cartDrawer.show === 'function') cartDrawer.show();
          });
        } else if (cartDrawer && typeof cartDrawer.show === 'function') {
          cartDrawer.show();
        }
      })
      .catch(function () {
        if (btn) btn.classList.add('wl-size--error');
        setTimeout(function () { if (btn) btn.classList.remove('wl-size--error'); }, 1500);
      })
      .finally(function () {
        if (btn) {
          btn.classList.remove('wl-size--loading');
          btn.disabled = false;
        }
      });
    }
  };

  /* ── Branded login modal (snippets/wishlist-login-modal.liquid) ── */
  function loginBase() {
    var cta = document.getElementById('WishlistLoginCta');
    if (cta) {
      if (!cta.dataset.base) cta.dataset.base = cta.getAttribute('href') || '/account/login';
      return cta.dataset.base;
    }
    return '/account/login';
  }
  function loginUrlWithReturn(retPath) {
    var base = loginBase();
    var ret = encodeURIComponent(retPath || (window.location.pathname + window.location.search));
    return base + (base.indexOf('?') >= 0 ? '&' : '?') + 'return_url=' + ret;
  }
  /* Return path stashed across the login redirect so we can bring the visitor
     back to where they hearted from. New customer accounts STRIP ?return_url
     (the /account/login 302 to the hosted login drops every query param), so we
     handle the return client-side via this cookie instead. Cookie survives the
     cross-subdomain auth hop (host-only, SameSite=Lax, sent on top-level nav back). */
  var WL_RETURN_COOKIE = 'threohsix_wl_return';
  function stashReturn() { try { setCookie(WL_RETURN_COOKIE, window.location.pathname + window.location.search, 1); } catch (e) {} }
  function readReturn() {
    var r = getCookie(WL_RETURN_COOKIE);
    // Only same-origin absolute paths ("/…"); reject "//host" and full URLs (open-redirect guard).
    return (r && r.charAt(0) === '/' && r.charAt(1) !== '/') ? r : null;
  }
  function clearReturn() { delCookie(WL_RETURN_COOKIE); }

  function escClose(e) { if (e.key === 'Escape' || e.key === 'Esc') closeLoginModal(); }
  function openLoginModal(retPath) {
    var modal = document.getElementById('WishlistLoginModal');
    if (retPath) { try { setCookie(WL_RETURN_COOKIE, retPath, 1); } catch (e) {} }
    else { stashReturn(); }
    if (!modal) { window.location.href = loginUrlWithReturn(retPath); return; }
    var cta = document.getElementById('WishlistLoginCta');
    if (cta) cta.href = loginUrlWithReturn(retPath);
    modal.hidden = false;
    void modal.offsetWidth; // reflow so the transition runs
    modal.classList.add('is-open');
    document.addEventListener('keydown', escClose);
  }
  function closeLoginModal() {
    clearReturn(); // visitor declined the login → don't auto-redirect them later
    var modal = document.getElementById('WishlistLoginModal');
    if (!modal) return;
    modal.classList.remove('is-open');
    document.removeEventListener('keydown', escClose);
    setTimeout(function () { if (!modal.classList.contains('is-open')) modal.hidden = true; }, 240);
  }
  window.Wishlist.openLoginModal = openLoginModal;
  window.Wishlist.closeLoginModal = closeLoginModal;

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-wl-login-close]')) { e.preventDefault(); closeLoginModal(); }
  });

  /* ── Global click handler for heart buttons ── */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wishlist-handle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    // Logged out: gate. Remember the clicked item, prompt login, save nothing yet.
    if (!STATE.loggedIn) {
      stashPending(readHeartPayload(btn));
      openLoginModal();
      return;
    }

    /* Prevent rapid double-fires */
    if (btn.dataset.wishlistBusy) return;
    btn.dataset.wishlistBusy = '1';
    setTimeout(function () { delete btn.dataset.wishlistBusy; }, 300);

    var p = readHeartPayload(btn);
    Wishlist.toggle(p.handle, p.title, p.url, p.image, p.price, p.variantId, p.variantTitle);
  });

  /* ── Re-sync hearts when Swiper creates cloned slides ── */
  var observer = new MutationObserver(function () {
    Wishlist.sync();
  });
  observer.observe(document.body, { childList: true, subtree: true });

  /* Throttle the MutationObserver sync to avoid excessive calls */
  var _origSync = Wishlist.sync;
  var _syncTimer = null;
  Wishlist.sync = function () {
    if (_syncTimer) clearTimeout(_syncTimer);
    _syncTimer = setTimeout(function () {
      _origSync.call(Wishlist);
      _syncTimer = null;
    }, 50);
  };
  /* Keep an immediate version for after toggle */
  Wishlist._syncNow = _origSync;

  /* ── Init: seed the in-memory mirror, merge legacy/pending once, paint ── */
  function initWishlist() {
    if (STATE.loggedIn) {
      /* Save-for-later nach Login: die Cart-Line MUSS weg, bevor die Seite gerendert
         wird, die die Besucherin danach sieht. Später wäre das gerenderte Cart-Markup
         dauerhaft veraltet — und weil das Theme Zeilen per INDEX entfernt (cart.js
         updateQuantity -> /cart/change.js {line}), würde ein Klick darauf die falsche
         Server-Zeile treffen und andere Artikel mit abräumen. Also: entfernen,
         Antwort abwarten, dann auf einer frisch gerenderten Seite landen. */
      var sfl = readPending();
      if (sfl && sfl.sflRemoveCart) {
        var sflVid = sfl.sflRemoveCart;
        delete sfl.sflRemoveCart;
        stashPending(sfl); // Marker verbraucht; das Item bleibt pending für die frische Seite
        var sflUpd = {};
        sflUpd[sflVid] = 0;
        var sflLand = function () {
          var to = readReturn();
          if (to) { clearReturn(); window.location.replace(to); }
          else { window.location.reload(); }
        };
        try {
          window.fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: sflUpd })
          }).then(sflLand, sflLand);
        } catch (e) { sflLand(); }
        return; // diese Seite wird ersetzt — hier darf nichts weiter laufen
      }

      // After a gated login: if we owe the visitor a redirect back to where they
      // hearted from, do it BEFORE committing pending. The pending heart then gets
      // committed AND painted on the DESTINATION page (client-side from the cookie,
      // so it shows instantly regardless of backend write timing) — not on this
      // throwaway intermediate page. Pending cookie is left intact for the destination.
      var back = readReturn();
      if (back) {
        clearReturn(); // single-use → no redirect loops
        var here = window.location.pathname + window.location.search;
        if (back !== here) { window.location.replace(back); return; }
      }

      var changed = false;
      if (Array.isArray(SERVER)) {
        // Backend is authoritative for this customer.
        mirror = SERVER.slice();
        if (!lsGet(SYNCED_FLAG)) {
          // One-time migration: fold the pre-login anonymous list AND anything the
          // per-customer cache accumulated while the backend wasn't live yet.
          changed = foldInto(mirror, legacyAnon());
          changed = foldInto(mirror, readCache()) || changed;
          lsSet(SYNCED_FLAG, '1');
          clearLegacyAnon();
        }
      } else {
        // Logged in but no server list yet (metafield empty / backend not live).
        // The per-customer cache is the store; still show the pre-login list.
        mirror = readCache();
        if (!lsGet(SYNCED_FLAG)) {
          changed = foldInto(mirror, legacyAnon()) || changed;
          // Do NOT set the synced flag until the server is authoritative.
        }
      }
      // Commit an item the visitor hearted right before logging in.
      var pending = readPending();
      if (pending) {
        if (foldInto(mirror, [pending])) changed = true;
        clearPending();
      }

      writeCache(mirror);
      if (changed) scheduleWrite();
    } else {
      mirror = [];
    }
    _origSync.call(Wishlist);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWishlist);
  } else {
    initWishlist();
  }

  /* ── Auto-remove from wishlist when added to cart ──
     Compares wishlist variant ids against current cart items after each
     cart update and removes anything that's now in the cart. Routes through
     save() so the removal persists server-side too. */
  function pruneWishlistFromCart(cart) {
    if (!STATE.loggedIn || !cart || !Array.isArray(cart.items)) return;
    var items = load();
    if (!items.length) return;
    var inCart = {};
    cart.items.forEach(function (li) {
      if (li && li.variant_id != null) inCart[String(li.variant_id)] = true;
    });
    var next = items.filter(function (i) { return !inCart[String(i.variantId)]; });
    if (next.length !== items.length) {
      save(next);
      _origSync.call(Wishlist);
    }
  }

  function subscribeCartUpdates() {
    if (!window.FoxTheme || !FoxTheme.pubsub || !FoxTheme.pubsub.subscribe) {
      return setTimeout(subscribeCartUpdates, 200);
    }
    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (data) {
      if (data && data.cart) pruneWishlistFromCart(data.cart);
    });
  }
  subscribeCartUpdates();

  /* Fallback: also intercept /cart/add.js + /cart/add fetches directly,
     in case something bypasses the pubsub (e.g. custom forms). */
  var _fetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var result = _fetch.apply(this, arguments);
    if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {
      result.then(function () {
        _fetch('/cart.js').then(function (r) { return r.json(); }).then(pruneWishlistFromCart).catch(function () {});
      }).catch(function () {});
    }
    return result;
  };
})();
